import { useCallback, useEffect, useLayoutEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { isWebView, isMobile } from '@manychat/manyui'

import { updateWebPushConsent } from 'common/core/actions/appActions'
import { addPrompt, removePrompt } from 'common/core/actions/uiActions'
import { WebPushConsent } from 'common/core/interfaces/userConsent'
import { getUser, getUserId, getUserWebPushConsent } from 'common/core/selectors/appSelectors'
import { engagementAPI } from 'shared/api/requests/engagement'
import { useUsercentrics } from 'utils/commonHooks'
import { UserFlag } from 'utils/services/featureFlagsService/constants'
import { useUserReleaseToggle } from 'utils/services/featureFlagsService/releaseTogglesHooks'

import { engagementService } from './brazeEngagementService'
import { SESSION_BRAZE_KEY } from './constants'
import { usePromptsAllowed } from './usePromptsAllowed'
import {
  getPushNotificationsPrompt,
  getBrazePrompt,
  BRAZE_PUSH_NOTIFICATION_PROMPT_ID,
  BRAZE_COOKIE_PROMPT,
} from './utils'

const USERCENTRICS_BRAZE_NAME = 'Braze'

export const useEngagementService = (isSilent: boolean = false) => {
  const { usercentricsServicesInfo, updateServicesConsents } =
    useUsercentrics(USERCENTRICS_BRAZE_NAME)
  const isBrazeProvided = usercentricsServicesInfo[USERCENTRICS_BRAZE_NAME]?.status ?? false
  const userId = useAppSelector(getUserId)
  const brazeId = usercentricsServicesInfo[USERCENTRICS_BRAZE_NAME]?.id ?? ''
  const dispatch = useAppDispatch()
  const webPushConsent = useAppSelector(getUserWebPushConsent)
  const isPromptsAllowed = usePromptsAllowed()

  const sessionBrazeValue = sessionStorage.getItem(SESSION_BRAZE_KEY)
  const isPushGranted = engagementService.isPushGranted
  const shouldDisable = Boolean(
    useAppSelector(getUser)?.flags?.[UserFlag.DISABLE_BRAZE_NOTIFICATIONS_FOR_WEB_VIEW],
  )

  const isPromptingEnabled = !shouldDisable || !isWebView
  const hasBrazeUsercentricsInfo = Boolean(usercentricsServicesInfo[USERCENTRICS_BRAZE_NAME])

  const shouldShowBrazePrompt =
    isPromptingEnabled &&
    userId &&
    !isBrazeProvided &&
    hasBrazeUsercentricsInfo &&
    !isSilent &&
    !sessionBrazeValue &&
    isPromptsAllowed &&
    !isMobile
  const isPushNotificationsEnabled = useUserReleaseToggle(UserFlag.ENABLE_BRAZE_PUSH_NOTIFICATIONS)
  const shouldShowPushPermissionRequest =
    isPromptingEnabled &&
    userId &&
    isBrazeProvided &&
    !isSilent &&
    hasBrazeUsercentricsInfo &&
    isPushGranted === null &&
    isPushNotificationsEnabled &&
    isPromptsAllowed

  const handleActivatePushNotification = useCallback(() => {
    engagementService.requestPushPermission()
  }, [])
  const handleDeniedPushNotification = useCallback(() => {
    engagementService.setIsPushGranted(false)
  }, [])

  const handleActivateBraze = useCallback(
    () => updateServicesConsents([{ id: brazeId, consent: true }]),
    [brazeId, updateServicesConsents],
  )
  const handleUpdateConsent = useCallback(
    (consent: WebPushConsent) => {
      dispatch(updateWebPushConsent(consent))
    },
    [dispatch],
  )

  useEffect(() => {
    if (shouldShowBrazePrompt) {
      const prompt = getBrazePrompt(handleActivateBraze)
      dispatch(addPrompt(prompt))
    } else {
      dispatch(removePrompt(BRAZE_COOKIE_PROMPT))
    }
  }, [dispatch, handleActivateBraze, shouldShowBrazePrompt])

  useEffect(() => {
    if (shouldShowPushPermissionRequest) {
      const prompt = getPushNotificationsPrompt(
        handleActivatePushNotification,
        handleDeniedPushNotification,
      )

      dispatch(addPrompt(prompt))
    } else {
      dispatch(removePrompt(BRAZE_PUSH_NOTIFICATION_PROMPT_ID))
    }
  }, [
    dispatch,
    handleActivatePushNotification,
    handleDeniedPushNotification,
    shouldShowPushPermissionRequest,
  ])

  useLayoutEffect(() => {
    if (!isBrazeProvided || !userId) {
      engagementService.dispose()

      return
    }

    const token = window.__INIT__['app.braze_jwt_token'] || ''
    const getToken = async (): Promise<string> => {
      const response = await engagementAPI.getToken()

      return response.data.jwt_token
    }

    engagementService.init({
      userId,
      token,
      getToken,
      consent: webPushConsent,
      updateConsent: handleUpdateConsent,
    })
  }, [handleUpdateConsent, isBrazeProvided, userId, webPushConsent])

  return engagementService
}
